/**
 * Chirp and Chirplet signal generation utilities
 * 
 * A chirp is a signal where frequency changes over time.
 * A chirplet is a windowed chirp - it only exists for a short duration.
 */

/**
 * Generate a linear chirp signal
 * Frequency sweeps linearly from f0 to f1 over the duration
 * 
 * @param {number} f0 - Starting frequency (in cycles per sample period)
 * @param {number} f1 - Ending frequency (in cycles per sample period)
 * @param {number} numSamples - Number of samples to generate
 * @param {number} amplitude - Signal amplitude (default 1)
 * @returns {Array<number>} - Array of signal values
 */
export function generateLinearChirp(f0, f1, numSamples, amplitude = 1) {
    const signal = [];
    const chirpRate = (f1 - f0) / numSamples;
    
    for (let i = 0; i < numSamples; i++) {
        const t = i / numSamples;
        // Instantaneous frequency: f(t) = f0 + chirpRate * t
        // Phase integral: phi(t) = 2*pi * (f0*t + 0.5*chirpRate*t^2)
        const phase = 2 * Math.PI * (f0 * t * numSamples + 0.5 * chirpRate * i * t * numSamples);
        signal.push(amplitude * Math.sin(phase));
    }
    return signal;
}

/**
 * Generate a chirplet (Gaussian-windowed chirp)
 * 
 * @param {number} t0 - Center time (0 to 1, normalized)
 * @param {number} f0 - Center frequency (in cycles per sample period)
 * @param {number} chirpRate - Rate of frequency change (positive = up-chirp)
 * @param {number} sigma - Gaussian window width (in samples)
 * @param {number} numSamples - Total number of samples
 * @param {number} amplitude - Signal amplitude (default 1)
 * @returns {Array<number>} - Array of signal values
 */
export function generateChirplet(t0, f0, chirpRate, sigma, numSamples, amplitude = 1) {
    const signal = [];
    const centerSample = t0 * numSamples;
    
    for (let i = 0; i < numSamples; i++) {
        const dt = i - centerSample;
        
        // Gaussian window
        const window = Math.exp(-(dt * dt) / (2 * sigma * sigma));
        
        // Chirp phase: f(t) = f0 + chirpRate * dt
        // Phase integral: phi = 2*pi * (f0*dt + 0.5*chirpRate*dt^2)
        const phase = 2 * Math.PI * (f0 * dt + 0.5 * chirpRate * dt * dt);
        
        signal.push(amplitude * window * Math.sin(phase));
    }
    return signal;
}

/**
 * Generate a complex chirplet (returns both real and imaginary parts)
 * Useful for chirplet transform matching
 * 
 * @param {number} t0 - Center time (0 to 1, normalized)
 * @param {number} f0 - Center frequency
 * @param {number} chirpRate - Rate of frequency change
 * @param {number} sigma - Gaussian window width
 * @param {number} numSamples - Total number of samples
 * @returns {Object} - {real: Array, imag: Array}
 */
export function generateComplexChirplet(t0, f0, chirpRate, sigma, numSamples) {
    const real = [];
    const imag = [];
    const centerSample = t0 * numSamples;
    
    for (let i = 0; i < numSamples; i++) {
        const dt = i - centerSample;
        
        // Gaussian window
        const window = Math.exp(-(dt * dt) / (2 * sigma * sigma));
        
        // Phase
        const phase = 2 * Math.PI * (f0 * dt + 0.5 * chirpRate * dt * dt);
        
        real.push(window * Math.cos(phase));
        imag.push(window * Math.sin(phase));
    }
    return { real, imag };
}

/**
 * Compute the inner product (correlation) between a signal and a chirplet
 * This is the core of chirplet matching
 * 
 * @param {Array<number>} signal - Input signal
 * @param {number} t0 - Chirplet center time
 * @param {number} f0 - Chirplet center frequency
 * @param {number} chirpRate - Chirplet chirp rate
 * @param {number} sigma - Chirplet window width
 * @returns {Object} - {magnitude: number, phase: number}
 */
export function chirpletMatch(signal, t0, f0, chirpRate, sigma) {
    const numSamples = signal.length;
    const chirplet = generateComplexChirplet(t0, f0, chirpRate, sigma, numSamples);
    
    let realSum = 0;
    let imagSum = 0;
    
    for (let i = 0; i < numSamples; i++) {
        realSum += signal[i] * chirplet.real[i];
        imagSum += signal[i] * chirplet.imag[i];
    }
    
    // Normalize by chirplet energy
    let energy = 0;
    for (let i = 0; i < numSamples; i++) {
        energy += chirplet.real[i] * chirplet.real[i] + chirplet.imag[i] * chirplet.imag[i];
    }
    energy = Math.sqrt(energy);
    
    if (energy > 0) {
        realSum /= energy;
        imagSum /= energy;
    }
    
    const magnitude = Math.sqrt(realSum * realSum + imagSum * imagSum);
    const phase = Math.atan2(imagSum, realSum);
    
    return { magnitude, phase };
}

/**
 * Compute a simple spectrogram using short-time Fourier transform
 * 
 * @param {Array<number>} signal - Input signal
 * @param {number} windowSize - Size of analysis window
 * @param {number} hopSize - Number of samples between windows
 * @returns {Array<Array<number>>} - 2D array [time][frequency] of magnitudes
 */
export function computeSpectrogram(signal, windowSize, hopSize) {
    const numWindows = Math.floor((signal.length - windowSize) / hopSize) + 1;
    const spectrogram = [];
    
    for (let w = 0; w < numWindows; w++) {
        const startIdx = w * hopSize;
        const spectrum = [];
        
        // Simple DFT for each window
        for (let k = 0; k < windowSize / 2; k++) {
            let real = 0;
            let imag = 0;
            
            for (let n = 0; n < windowSize; n++) {
                const idx = startIdx + n;
                if (idx < signal.length) {
                    // Hann window
                    const window = 0.5 * (1 - Math.cos(2 * Math.PI * n / (windowSize - 1)));
                    const angle = -2 * Math.PI * k * n / windowSize;
                    real += signal[idx] * window * Math.cos(angle);
                    imag += signal[idx] * window * Math.sin(angle);
                }
            }
            
            spectrum.push(Math.sqrt(real * real + imag * imag) / windowSize);
        }
        
        spectrogram.push(spectrum);
    }
    
    return spectrogram;
}

/**
 * Generate preset signals for demos
 */
export const presetSignals = {
    /**
     * Simple linear up-chirp
     */
    linearChirp: (numSamples = 256) => {
        return generateLinearChirp(0.01, 0.15, numSamples);
    },
    
    /**
     * Two chirps crossing (one up, one down)
     */
    crossingChirps: (numSamples = 256) => {
        const upChirp = generateLinearChirp(0.02, 0.12, numSamples, 0.7);
        const downChirp = generateLinearChirp(0.12, 0.02, numSamples, 0.7);
        return upChirp.map((v, i) => v + downChirp[i]);
    },
    
    /**
     * Chirp with a steady tone
     */
    chirpPlusTone: (numSamples = 256) => {
        const chirp = generateLinearChirp(0.02, 0.12, numSamples, 0.6);
        const tone = [];
        const toneFreq = 0.07; // Steady frequency in the middle
        for (let i = 0; i < numSamples; i++) {
            tone.push(0.5 * Math.sin(2 * Math.PI * toneFreq * i));
        }
        return chirp.map((v, i) => v + tone[i]);
    },
    
    /**
     * LIGO-inspired chirp (exponentially increasing frequency)
     */
    ligoChirp: (numSamples = 256) => {
        const signal = [];
        for (let i = 0; i < numSamples; i++) {
            const t = i / numSamples;
            // Frequency increases exponentially toward the end
            const f = 0.02 + 0.15 * Math.pow(t, 3);
            // Amplitude also increases
            const amp = 0.3 + 0.7 * t;
            // Phase is integral of frequency
            const phase = 2 * Math.PI * (0.02 * i + 0.15 * Math.pow(t, 4) * numSamples / 4);
            signal.push(amp * Math.sin(phase));
        }
        return signal;
    },
    
    /**
     * Bird song-like signal (multiple chirps with different rates)
     */
    birdSong: (numSamples = 256) => {
        const signal = new Array(numSamples).fill(0);
        
        // Add a few chirplets at different times
        const chirps = [
            { t0: 0.15, f0: 0.08, rate: 0.0005, sigma: 15 },
            { t0: 0.35, f0: 0.12, rate: -0.0003, sigma: 20 },
            { t0: 0.55, f0: 0.06, rate: 0.0008, sigma: 12 },
            { t0: 0.75, f0: 0.10, rate: 0.0002, sigma: 18 },
        ];
        
        for (const c of chirps) {
            const chirplet = generateChirplet(c.t0, c.f0, c.rate, c.sigma, numSamples, 0.8);
            for (let i = 0; i < numSamples; i++) {
                signal[i] += chirplet[i];
            }
        }
        
        return signal;
    }
};

/**
 * Get instantaneous frequency at a given time for a linear chirp
 * 
 * @param {number} t - Time (0 to 1)
 * @param {number} f0 - Starting frequency
 * @param {number} f1 - Ending frequency
 * @returns {number} - Instantaneous frequency
 */
export function getInstantaneousFrequency(t, f0, f1) {
    return f0 + (f1 - f0) * t;
}

/**
 * Get the phase at time t for a chirplet
 * 
 * @param {number} t - Time (sample index)
 * @param {number} t0 - Center time (sample index)
 * @param {number} f0 - Center frequency
 * @param {number} chirpRate - Rate of frequency change
 * @returns {number} - Phase in radians
 */
export function getChirpletPhase(t, t0, f0, chirpRate) {
    const dt = t - t0;
    return 2 * Math.PI * (f0 * dt + 0.5 * chirpRate * dt * dt);
}

/**
 * Get the instantaneous angular velocity at time t for a chirplet
 * 
 * @param {number} t - Time (sample index)
 * @param {number} t0 - Center time (sample index)
 * @param {number} f0 - Center frequency
 * @param {number} chirpRate - Rate of frequency change
 * @returns {number} - Angular velocity (radians per sample)
 */
export function getChirpletAngularVelocity(t, t0, f0, chirpRate) {
    const dt = t - t0;
    return 2 * Math.PI * (f0 + chirpRate * dt);
}
